import { useEffect, useState } from 'react';
import { ARS_DENOMINATIONS, CASH_MOVEMENT_TYPES, countTotal, uuidv7, type CashMovementType, type CashSummary } from '@pos/core';
import { applyLocalChanges, type CashSessionRow } from '@pos/local-db';
import { useSession } from '../../app/current.tsx';
import { db } from '../../lib/db.ts';
import { requestSync } from '../../services/sync.ts';
import { Alert, Button, Field, Input, cx, errorMessage } from '../../ui/index.tsx';
import { Dialog } from '../../ui/Dialog.tsx';
import { MoneyInput } from '../../ui/MoneyInput.tsx';
import { useMoney } from '../products/data.ts';
import { supabase } from '../../lib/supabase.ts';
import { useOnline } from '../../lib/use-online.ts';
import { useServerUserId } from '../../lib/server-session.ts';
import { syncEngine } from '../../services/sync.ts';
import { fetchCashSummary, useCurrentRegister, useLastClosedSession } from './data.ts';

// ---------------------------------------------------------------------------
// Abrir caja
// ---------------------------------------------------------------------------

export function OpenCashDialog({ open, onClose, onOpened }: { open: boolean; onClose: () => void; onOpened?: () => void }) {
  const { business, device, writer } = useSession();
  const register = useCurrentRegister();
  const last = useLastClosedSession();
  const money = useMoney();
  const online = useOnline();
  const serverUser = useServerUserId();
  const [amount, setAmount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadyOpen, setAlreadyOpen] = useState<string | null>(null);
  // Con conexión se abre la caja compartida; sin conexión, una caja solo de este dispositivo.
  const shared = online && serverUser === writer.user_id;

  useEffect(() => {
    if (open) {
      setAmount(last?.opening_amount ?? null);
      setError(null);
      setAlreadyOpen(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (amount === null || amount < 0) return setError('Escribí el fondo inicial (puede ser $0).');
    setBusy(true);
    setError(null);
    try {
      if (shared) {
        const { data, error: rpcError } = await supabase.rpc('open_cash_session', {
          p_id: uuidv7(),
          p_business_id: business.id,
          p_register_id: register?.id ?? null,
          p_opening_amount: amount,
          p_device_id: device.id,
        });
        if (rpcError) throw new Error(rpcError.message);
        const result = data as { status: 'ok' | 'already_open'; session: CashSessionRow };
        await db.cash_sessions.put(result.session);
        if (result.status === 'already_open') {
          // Otro dispositivo ya la abrió: se usa esa (con su fondo).
          const opener = (await db.memberships.where('user_id').equals(result.session.opened_by ?? '').first())?.display_name;
          setAlreadyOpen(
            `Esta caja ya estaba abierta${opener ? ` (la abrió ${opener})` : ''} con un fondo de ${money(result.session.opening_amount)}. Ya la estás usando en este dispositivo.`,
          );
          return;
        }
      } else {
        const own = await db.cash_sessions.where('device_id').equals(device.id).filter((s) => s.closed_at === null && !!s.device_only).count();
        if (own) throw new Error('Ya hay una caja abierta en este dispositivo.');
        const now = new Date().toISOString();
        const row: CashSessionRow = {
          id: uuidv7(),
          business_id: business.id,
          register_id: register?.id ?? null,
          device_id: device.id,
          device_only: true,
          opened_by: writer.user_id,
          opened_at: now,
          opening_amount: amount,
          closed_at: null,
          counted_cash: null,
          close_notes: null,
          created_at: now,
          updated_at: now,
        };
        // Al servidor van solo los datos de apertura: quién abrió lo pone él.
        await applyLocalChanges(db, [{ table: 'cash_sessions', kind: 'insert', row: row as never }], writer);
        requestSync(0);
      }
      onOpened?.();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (alreadyOpen) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        title="La caja ya estaba abierta"
        footer={
          <Button
            onClick={() => {
              onOpened?.();
              onClose();
            }}
          >
            Listo
          </Button>
        }
      >
        <Alert tone="info">{alreadyOpen}</Alert>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Abrir caja"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={busy}>
            Abrir caja
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {register && <p className="text-sm text-slate-600">Caja: <strong>{register.name}</strong></p>}
        <Field label="Fondo inicial (efectivo en la caja al empezar)" hint={last ? `La caja anterior abrió con ${money(last.opening_amount)}.` : 'Lo que hay para dar vuelto.'}>
          {(id) => <MoneyInput id={id} autoFocus value={amount} onChange={setAmount} />}
        </Field>
        {!shared && (
          <Alert tone="warning">
            Sin conexión no se puede saber si otro dispositivo ya abrió la caja: se abre una caja <strong>solo para este dispositivo</strong>. Se cierra
            desde acá, aparte de la compartida.
          </Alert>
        )}
        {error && <Alert tone="error">{error}</Alert>}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Ingreso / retiro / gasto
// ---------------------------------------------------------------------------

export function CashMovementDialog({ session, type, onClose }: { session: CashSessionRow; type: CashMovementType | null; onClose: () => void }) {
  const { business, device, writer } = useSession();
  const [amount, setAmount] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAmount(null);
    setReason('');
    setError(null);
  }, [type]);

  if (!type) return <Dialog open={false} onClose={onClose} title="" children={null} />;
  const def = CASH_MOVEMENT_TYPES[type];

  const submit = async () => {
    if (!amount || amount <= 0) return setError('Escribí el importe.');
    if (reason.trim().length < 2) return setError('Escribí el motivo (queda registrado).');
    setBusy(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      await applyLocalChanges(
        db,
        [
          {
            table: 'cash_movements',
            kind: 'insert',
            row: { id: uuidv7(), business_id: business.id, session_id: session.id, type, amount, reason: reason.trim(), occurred_at: now, device_id: device.id, created_by: writer.user_id, created_at: now },
          },
        ],
        writer,
      );
      requestSync(0);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={def.label}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} loading={busy}>
            Guardar
          </Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Importe en efectivo">{(id) => <MoneyInput id={id} autoFocus value={amount} onChange={setAmount} />}</Field>
        <Field label="Motivo" hint={def.hint}>
          {(id) => <Input id={id} value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        {error && <Alert tone="error">{error}</Alert>}
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Cerrar caja (arqueo)
// ---------------------------------------------------------------------------

export function CloseCashDialog({
  session,
  summary,
  open,
  blind,
  onClose,
  onClosed,
}: {
  session: CashSessionRow;
  summary: CashSummary;
  open: boolean;
  /** Arqueo ciego: no se muestra cuánto debería haber hasta cerrar. */
  blind: boolean;
  onClose: () => void;
  onClosed: (counted: number, difference: number, expected: number | null) => void;
}) {
  const { writer } = useSession();
  const money = useMoney();
  const online = useOnline();
  const serverUser = useServerUserId();
  // Caja compartida: el cierre lo calcula el servidor con TODOS los dispositivos (hace falta conexión).
  const shared = !session.device_only;
  const canClose = !shared || (online && serverUser === writer.user_id);
  const [serverExpected, setServerExpected] = useState<number | null | undefined>(undefined);
  const [byBills, setByBills] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [total, setTotal] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCounts({});
      setTotal(null);
      setNotes('');
      setError(null);
      setServerExpected(undefined);
    }
  }, [open]);

  // Antes de cerrar la compartida: se envía lo pendiente de este dispositivo y se pide el esperado.
  useEffect(() => {
    if (!open || !shared || !canClose) return;
    let cancelled = false;
    void (async () => {
      try {
        await syncEngine.sync();
        const s = await fetchCashSummary(session.id);
        if (!cancelled) setServerExpected(s.expected);
      } catch (e) {
        // Igual se puede cerrar: el esperado definitivo lo calcula el servidor al cerrar.
        if (!cancelled) {
          setError(errorMessage(e));
          setServerExpected(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, shared, canClose, session.id]);

  const expected = shared ? (serverExpected ?? null) : summary.expected;
  const loadingExpected = shared && serverExpected === undefined && !blind;
  const counted = byBills ? countTotal(counts) : total;
  const difference = counted === null || expected === null ? null : counted - expected;

  const submit = async () => {
    if (counted === null || counted < 0) return setError('Contá el efectivo y escribí el total.');
    if (difference !== null && difference !== 0 && !blind && notes.trim().length < 2) {
      // Con diferencia se pide una nota, para que el dueño sepa qué pasó.
      return setError('Hay diferencia: escribí una nota (ej.: "faltan $200, se dio mal un vuelto").');
    }
    setBusy(true);
    setError(null);
    try {
      if (shared) {
        const { data, error: rpcError } = await supabase.rpc('close_cash_session', { p_id: session.id, p_counted: counted, p_notes: notes.trim() || null });
        if (rpcError) throw new Error(rpcError.message);
        const result = data as { status: 'ok' | 'already_closed'; session: CashSessionRow };
        await db.cash_sessions.put(result.session);
        if (result.status === 'already_closed') throw new Error('Otro dispositivo ya cerró esta caja.');
        onClosed(counted, result.session.difference ?? 0, result.session.expected_cash ?? null);
        return;
      }
      await applyLocalChanges(
        db,
        [{ table: 'cash_sessions', kind: 'patch', id: session.id, changes: { closed_at: new Date().toISOString(), counted_cash: counted, close_notes: notes.trim() || null } }],
        writer,
      );
      // El esperado y la diferencia definitivos los calcula el servidor; mientras, se muestra el cálculo local.
      await db.cash_sessions.update(session.id, { closed_by: writer.user_id, expected_cash: summary.expected, difference: counted - summary.expected });
      requestSync(0);
      onClosed(counted, counted - summary.expected, summary.expected);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Cerrar caja"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={submit} loading={busy} disabled={counted === null || !canClose || loadingExpected}>
            Cerrar caja
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        {!canClose && (
          <Alert tone="warning">
            Para cerrar esta caja hace falta conexión: el cierre suma las ventas de todos los dispositivos que la usaron.
          </Alert>
        )}
        {shared && canClose && <p className="text-xs text-slate-500">El esperado lo calcula el servidor con lo de todos los dispositivos que usaron esta caja.</p>}
        <div className="flex items-center justify-between">
          <span className="font-medium text-slate-700">¿Cuánto efectivo hay en la caja?</span>
          <button type="button" className="font-medium text-brand" onClick={() => setByBills((b) => !b)}>
            {byBills ? 'Escribir el total' : 'Contar por billetes'}
          </button>
        </div>
        {byBills ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {ARS_DENOMINATIONS.map((value) => (
              <label key={value} className="flex items-center gap-2">
                <span className="w-16 shrink-0 text-right text-slate-600 tabular-nums">{money(value)}</span>
                <span className="text-slate-400">×</span>
                <div className="w-16">
                  <Input
                    inputMode="numeric"
                    compact
                    placeholder="0"
                    aria-label={`Cantidad de ${money(value)}`}
                    value={counts[value] ? String(counts[value]) : ''}
                    onChange={(e) => setCounts((c) => ({ ...c, [value]: Number(e.target.value.replace(/\D/g, '')) || 0 }))}
                  />
                </div>
              </label>
            ))}
          </div>
        ) : (
          <MoneyInput autoFocus value={total} onChange={setTotal} aria-label="Efectivo contado" />
        )}
        <div className="rounded-lg bg-slate-50 p-3">
          <Line label="Contado" value={counted === null ? '—' : money(counted)} strong />
          {!blind && (
            <>
              <Line label="Debería haber" value={loadingExpected ? 'Calculando…' : expected === null ? '—' : money(expected)} />
              {difference !== null && (
                <Line
                  label="Diferencia"
                  value={`${difference > 0 ? '+' : ''}${money(difference)}`}
                  className={difference === 0 ? 'text-emerald-700' : difference < 0 ? 'text-red-700' : 'text-amber-700'}
                />
              )}
            </>
          )}
          {blind && <p className="mt-1 text-xs text-slate-500">Arqueo ciego: vas a ver la diferencia después de cerrar.</p>}
        </div>
        <Field label="Notas del cierre (opcional)">
          {(id) => <Input id={id} value={notes} maxLength={300} placeholder="Ej.: se retiraron $20.000 para el banco" onChange={(e) => setNotes(e.target.value)} />}
        </Field>
        {error && <Alert tone="error">{error}</Alert>}
      </div>
    </Dialog>
  );
}

export function Line({ label, value, strong, className }: { label: string; value: string; strong?: boolean; className?: string }) {
  return (
    <div className={cx('flex justify-between gap-3', strong && 'text-base font-semibold', className)}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}
